/**
 * « Établissements » (admin) : état de l'annuaire (`public.schools`, migration 0705) et mise à jour depuis
 * l'Annuaire de l'éducation par l'edge function `import-schools` (aperçu sans écriture, ou import).
 *
 * Réponse de l'edge fn vérifiée par Zod (hors des types générés).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';

/** `schools` (0705) n'est pas dans les types générés. */
const db = supabase as unknown as SupabaseClient;

export const schoolsImportKeys = { stats: ['admin', 'schools-directory'] as const };

const counts = z.record(z.string(), z.number());

export const schoolsImportResultSchema = z.object({
  ok: z.boolean(),
  mode: z.enum(['preview', 'import']).optional(),
  message: z.string().optional(),
  sites: z.number().optional(),
  etablissements: z.number().optional(),
  types: counts.optional(),
  secteurs: counts.optional(),
  sansGps: z.number().optional(),
  outreMer: z.number().optional(),
  upserted: z.number().optional(),
  closed: z.number().optional(),
  importedAt: z.string().optional(),
  durationMs: z.number().optional(),
});

export type SchoolsImportResult = z.infer<typeof schoolsImportResultSchema>;
export type SchoolsImportMode = 'preview' | 'import';

export class SchoolsImportError extends Error {}

export async function runSchoolsImport(mode: SchoolsImportMode): Promise<SchoolsImportResult> {
  const { data, error } = await supabase.functions.invoke('import-schools', {
    body: { action: mode },
  });
  if (error) {
    const status = (error as { context?: Response }).context?.status;
    if (status === 404)
      throw new SchoolsImportError(
        'La fonction « import-schools » n’est pas encore déployée (supabase functions deploy import-schools).',
      );
    if (status === 401 || status === 403)
      throw new SchoolsImportError('Réservé aux admins : reconnecte-toi avec un compte admin.');
    throw new SchoolsImportError(`Le serveur n’a pas répondu (${error.message}).`);
  }
  const parsed = schoolsImportResultSchema.safeParse(data);
  if (!parsed.success) throw new SchoolsImportError('Réponse inattendue du serveur.');
  return parsed.data;
}

export interface SchoolsDirectoryStats {
  open: number;
  colleges: number;
  lycees: number;
  erea: number;
  closed: number;
  lastImportAt: string | null;
  /** Élèves qui ont choisi leur établissement (null si illisible). */
  studentsWithSchool: number | null;
}

async function countSchools(
  filter: (q: ReturnType<typeof baseCount>) => ReturnType<typeof baseCount>,
) {
  const { count, error } = await filter(baseCount());
  if (error) throw error;
  return count ?? 0;
}

function baseCount() {
  return db
    .from('schools')
    .select('id', { count: 'exact', head: true })
    .eq('source', 'annuaire-education');
}

export async function fetchSchoolsDirectoryStats(): Promise<SchoolsDirectoryStats> {
  const [open, colleges, lycees, erea, closed, last, students] = await Promise.all([
    countSchools((q) => q.eq('is_open', true)),
    countSchools((q) => q.eq('is_open', true).eq('kind', 'college')),
    countSchools((q) => q.eq('is_open', true).eq('kind', 'lycee')),
    countSchools((q) => q.eq('is_open', true).eq('kind', 'erea')),
    countSchools((q) => q.eq('is_open', false)),
    db
      .from('schools')
      .select('imported_at')
      .eq('source', 'annuaire-education')
      .order('imported_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('student_profiles')
      .select('id', { count: 'exact', head: true })
      .not('school_id', 'is', null),
  ]);
  return {
    open,
    colleges,
    lycees,
    erea,
    closed,
    lastImportAt: (last.data as { imported_at: string | null } | null)?.imported_at ?? null,
    studentsWithSchool: students.error ? null : (students.count ?? 0),
  };
}

export function useSchoolsDirectoryStats() {
  return useQuery({ queryKey: schoolsImportKeys.stats, queryFn: fetchSchoolsDirectoryStats });
}

export function useRunSchoolsImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runSchoolsImport,
    onSettled: () => void qc.invalidateQueries({ queryKey: schoolsImportKeys.stats }),
  });
}

/** « 14 837 » */
export const nb = (n: number | undefined | null) => (n ?? 0).toLocaleString('fr-FR');

/** Lignes lisibles d'un résultat (aperçu ou import). PUR. */
export function importResultLines(r: SchoolsImportResult): string[] {
  const lines: string[] = [];
  if (r.etablissements != null)
    lines.push(
      `${nb(r.etablissements)} établissements (${nb(r.sites)} sites) : ${nb(r.types?.college)} collèges, ${nb(r.types?.lycee)} lycées, ${nb(r.types?.erea)} EREA.`,
    );
  if (r.secteurs)
    lines.push(
      `${nb(r.secteurs.public)} publics, ${nb(r.secteurs.prive)} privés · ${nb(r.outreMer)} outre-mer · ${nb(r.sansGps)} sans GPS.`,
    );
  if (r.mode === 'import' && r.upserted != null)
    lines.push(
      `${nb(r.upserted)} écrits ou mis à jour, ${nb(r.closed)} fermé${(r.closed ?? 0) > 1 ? 's' : ''} (absents de l’annuaire).`,
    );
  if (r.durationMs != null) lines.push(`Durée : ${Math.round(r.durationMs / 100) / 10} s.`);
  return lines;
}
